import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Layers, Building, Users, MapPin, Image, CreditCard } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

/**
 * How It Works Page - LikeKind (LOCKED per directive)
 * 
 * Two popups:
 * 1. Categories - how discovery works
 * 2. Commons - what it is and isn't
 * 
 * Clear, literal language. Assume user knows nothing.
 */

export default function HowItWorks() {
  const navigate = useNavigate();
  const [activePopup, setActivePopup] = useState(null);

  const topics = [
    {
      id: 'categories',
      title: 'Categories',
      icon: Layers,
      description: 'How discovery works on LikeKind',
      content: {
        title: 'How Categories Work',
        sections: [
          {
            heading: 'What are Categories?',
            text: 'Categories are how you find people and places on LikeKind. They\'re organized by interest and activity: Nourish & Care, Sports, Learn & Create, Culture & Play, and Circles.'
          },
          {
            heading: 'How do I browse?',
            text: 'Tap a category to see Places and people related to that interest. You can filter by your location to find what\'s nearby. Each category may have subcategories for more specific interests.'
          },
          {
            heading: 'What will I see?',
            text: 'In each category, you\'ll see a chronological, finite feed of activity. This means: posts appear in the order they were made, you can reach the end (no infinite scroll), and nothing is ranked by money or engagement.'
          },
          {
            heading: 'Visual background',
            text: 'Categories may have subtle background images or colors to give each area a distinct feel. These are set by LikeKind and don\'t affect how content is displayed or ranked.'
          }
        ]
      }
    },
    {
      id: 'commons',
      title: 'Commons',
      icon: Building,
      description: 'What Commons is and what it isn\'t',
      content: {
        title: 'Understanding Commons',
        sections: [
          {
            heading: 'What is Commons?',
            text: 'Commons is a way to keep a Place steady over time. When a Place has been active and the steward wants to maintain it, they can opt in to Commons. The Place stays searchable, archived, and maintained.'
          },
          {
            heading: 'What Commons DOES:',
            text: '• Archives and persistence (the Place doesn\'t disappear)\n• Steward tools (basic moderation and management)\n• Governance controls (rules for the Place)\n• Continuity guarantees (the Place remains stable)\n• Succession support (if the steward leaves, the Place continues)'
          },
          {
            heading: 'What Commons does NOT do:',
            text: '• Does NOT change visibility (Commons Places aren\'t boosted)\n• Does NOT gate participation (anyone can still join public Places)\n• Does NOT add features to non-stewards\n• Does NOT affect feeds or discovery\n• Does NOT equal privacy (Places can be public or private regardless)'
          },
          {
            heading: 'Who pays for Commons?',
            text: 'The steward of a Place pays to keep it. They\'re paying for presence, continuity, and responsibility—not for reach, targeting, or amplification. All users experience the Place the same way.'
          }
        ]
      }
    },
    {
      id: 'connections',
      title: 'Connections',
      icon: Users,
      description: 'How to connect with others',
      content: {
        title: 'How Connections Work',
        sections: [
          {
            heading: 'Consent-based connections',
            text: 'Every connection on LikeKind requires consent from both people. There are no auto-connections, no auto-prompts, and no pressure to connect with anyone.'
          },
          {
            heading: '"Ready to connect"',
            text: 'When you visit someone\'s profile and want to reach out, tap "Ready to connect." This opens the Companion Connect app where you can introduce yourself.'
          },
          {
            heading: 'What is Companion Connect?',
            text: 'Companion Connect is a separate app for conversations. It keeps your LikeKind experience focused on discovery and sharing, while conversations happen in a dedicated space. Think of LikeKind as the lobby, and Companion Connect as the conversation room.'
          },
          {
            heading: 'Your connections (Circles)',
            text: 'People you\'ve connected with appear in your Circles. This is your community—the people you\'ve chosen to stay in touch with. Only you can see your full Circles list.'
          }
        ]
      }
    },
    {
      id: 'places',
      title: 'Places',
      icon: MapPin,
      description: 'Where interaction happens',
      content: {
        title: 'Understanding Places',
        sections: [
          {
            heading: 'What is a Place?',
            text: 'A Place is where interaction happens on LikeKind. It could be a local community, a group around a shared interest, or a space for a specific activity. Places are the heart of LikeKind.'
          },
          {
            heading: 'Public vs. Private',
            text: 'The creator of a Place chooses whether it\'s public (anyone can find and join) or private (invitation only). This is about visibility, not about features. Public and private Places work the same way.'
          },
          {
            heading: 'Temporary by default',
            text: 'Places start lightweight and temporary. They don\'t need to last forever. If a Place becomes important and active, the steward can choose to "keep" it through Commons.'
          },
          {
            heading: 'Stewards',
            text: 'Every Place has a steward—the person responsible for maintaining it. Stewards can set rules, moderate content, and decide whether to make the Place permanent through Commons.'
          }
        ]
      }
    },
    {
      id: 'placards',
      title: 'Placards & Support',
      icon: Image,
      description: 'How sponsorship works',
      content: {
        title: 'Placards & Sponsorship',
        sections: [
          {
            heading: 'What are placards?',
            text: 'Placards are static acknowledgements that appear in fixed spots: on profiles, category pages, or place pages. They\'re clearly labeled "Supported by" so you always know what\'s sponsored.'
          },
          {
            heading: 'What placards are NOT:',
            text: '• NOT in your feed (your feed is never monetized)\n• NOT targeted to you personally\n• NOT rotating based on performance\n• NOT optimized for clicks or engagement\n• NOT auctions or real-time bidding'
          },
          {
            heading: 'Same for everyone',
            text: 'Everyone sees the same placards in the same spots. There\'s no behavioral tracking, no personalization, and no data collected about what you look at or click on.'
          },
          {
            heading: 'Opt out',
            text: 'If you see a placard you\'d rather not see, you can opt out of that specific placement. We\'ll ask for a brief reason (to improve placement quality), and it will be removed from your view.'
          }
        ]
      }
    },
    {
      id: 'membership',
      title: 'Membership',
      icon: CreditCard,
      description: 'What membership means',
      content: {
        title: 'Membership on LikeKind',
        sections: [
          {
            heading: 'What is membership?',
            text: 'Membership is a commitment signal. It shows you\'re here on purpose, not just passing through. It supports LikeKind\'s development and maintenance.'
          },
          {
            heading: 'What membership does NOT do:',
            text: '• Does NOT boost your visibility\n• Does NOT change your feed placement\n• Does NOT affect ranking\n• Does NOT grant power over others\n\n"Membership shows you\'re here on purpose. It doesn\'t make you louder."'
          },
          {
            heading: 'Tiers',
            text: '🌱 Open Member: Browse, join groups, message anyone. Free.\n🧭 Personal Member: Everything in Open + personal timelines (SCUTA), Circles, private groups.\n🏛️ Commons: Make a group last — continuity support for groups that keep going.'
          },
          {
            heading: 'Invites',
            text: 'Paid members receive 3 invites to share with others. When someone you invite becomes a paid member, that invite is restored. No referral bonuses, no leaderboards, no gamification.'
          }
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button onClick={() => navigate('/settings')} className="p-2 -ml-2 rounded-lg hover:opacity-70 transition-opacity">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>How It Works</h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <p className="text-sm mb-6" style={{ color: 'var(--fy-text-secondary)' }}>
          Tap a topic to learn more. LikeKind is designed to be simple—if something feels confusing, let us know.
        </p>

        <div className="grid gap-3">
          {topics.map(topic => (
            <Card 
              key={topic.id}
              className="p-4 cursor-pointer hover:shadow-md transition-shadow"
              style={{ border: '1px solid var(--fy-border-subtle)' }}
              onClick={() => setActivePopup(topic)}
            >
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--lk-sage-light, #E8EDE5)' }}>
                  <topic.icon className="w-6 h-6" style={{ color: 'var(--lk-sage)' }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>{topic.title}</h3>
                  <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{topic.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </main>

      {/* Topic Popup Modal */}
      {activePopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setActivePopup(null)}>
          <Card 
            className="w-full max-w-lg max-h-[85vh] overflow-auto"
            style={{ backgroundColor: 'var(--fy-background-primary)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 p-4 border-b flex items-center justify-between" style={{ backgroundColor: 'var(--fy-background-primary)', borderColor: 'var(--fy-border-subtle)' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--lk-sage-light, #E8EDE5)' }}>
                  <activePopup.icon className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                </div>
                <h2 className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>{activePopup.content.title}</h2>
              </div>
              <button 
                onClick={() => setActivePopup(null)}
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {activePopup.content.sections.map((section, idx) => (
                <div key={idx}>
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--fy-text-primary)' }}>{section.heading}</h3>
                  <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--fy-text-secondary)' }}>
                    {section.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 p-4 border-t" style={{ backgroundColor: 'var(--fy-background-primary)', borderColor: 'var(--fy-border-subtle)' }}>
              <Button 
                onClick={() => setActivePopup(null)}
                className="w-full"
                style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
              >
                Got it
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
